/**
 * React Hook for WebSocket Session Management (migrated from WebRTC)
 * 
 * Provides React integration for WebSocket service with OpenAI Realtime API.
 * Handles state management, lifecycle, and event handling.
 * 
 * MIGRATION NOTE: This was migrated from WebRTC to WebSockets to fix role-switching issues.
 * The interface remains identical for backwards compatibility with components.
 * 
 * REFACTOR NOTE (2026-01): This hook has been refactored to compose smaller, focused hooks.
 * Each sub-hook handles a specific responsibility for better maintainability and testability.
 * 
 * XSTATE MIGRATION (2026-01): State management migrated to XState for explicit state machine.
 * All state transitions now go through the machine, preventing invalid states.
 */

import { useEffect, useCallback, useRef, useMemo } from 'react';
import { useMachine } from '@xstate/react';
import { createBrowserInspector } from '@statelyai/inspect';
import { WebSocketService } from '../services/realtime/WebSocketService';
import { WebAudioManager, AudioLevel } from '../services/audio';
import { logger } from '../utils/logger';

// Create inspector singleton (only in development browser environment)
// Note: Disabled for Jest tests which don't support import.meta
let inspector: ReturnType<typeof createBrowserInspector> | null = null;

// Helper to check if we're in development mode (Jest-compatible)
// Uses indirect eval to avoid Jest parser error with import.meta syntax
function isDevMode(): boolean {
  try {
    // Check for Vite dev mode flag
    if ((globalThis as any).__VITE_DEV_MODE__) return true;
    
    // Use Function constructor to evaluate import.meta without parser seeing it
    // This avoids "Cannot use 'import.meta' outside a module" in Jest
    const checkImportMeta = new Function('return typeof import.meta !== "undefined" && import.meta.env?.DEV');
    return !!checkImportMeta();
  } catch {
    return false;
  }
}

function initializeInspector() {
  // Skip in test environment
  if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'test') {
    return null;
  }
  
  // Only initialize in browser with development mode
  if (typeof window === 'undefined') {
    return null;
  }
  
  // 🚨 CRITICAL: Only enable inspector in development mode!
  // This was missing and caused popups in production (stately.ai/registry/inspect)
  if (!isDevMode()) {
    return null;
  }
  
  try {
    const created = createBrowserInspector();
    logger.info('[XState] 🔍 DevTools inspector initialized');
    return created;
  } catch (error) {
    console.warn('[XState] Failed to initialize inspector:', error);
    return null;
  }
}

inspector = initializeInspector();

// Import types from the new modular structure
import {
  WebSocketSessionConfig,
  WebSocketSessionState,
  UseWebSocketSessionReturn,
} from './websocket/types';

// Import the state machine
import {
  conversationMachine,
  getConnectionStatus,
  isUserSpeaking as machineIsUserSpeaking,
  isAISpeaking as machineIsAISpeaking,
  isConnected as machineIsConnected,
} from './websocket/conversationMachine';

// Re-export types for backwards compatibility
export type {
  AudioQualityStats,
  TranscriptMessage,
  WebSocketSessionState,
  WebSocketSessionConfig,
  UseWebSocketSessionReturn,
} from './websocket/types';

// Import sub-hooks
import { useConnectionManager } from './websocket/useConnectionManager';
import { useAudioController } from './websocket/useAudioController';
import { useTranscriptManager } from './websocket/useTranscriptManager';
import { useSpeechLifecycle } from './websocket/useSpeechLifecycle';
// useSpeechCorrelator removed - no longer needed (messages use server item_id directly)
import { useInputMode } from './websocket/useInputMode';
import { useAnalysisMode } from './websocket/useAnalysisMode';
import { useContextManager } from './websocket/useContextManager';

// ============================================================================
// Main Hook
// ============================================================================

const useWebSocketSession = (config: WebSocketSessionConfig = {}): UseWebSocketSessionReturn => {
  // XState machine for conversation state with optional DevTools inspection
  const machineOptions = useMemo(() => {
    const options: any = {};
    if (inspector) {
      options.inspect = inspector.inspect;
    }
    return options;
  }, []);
  
  const [machineState, send] = useMachine(conversationMachine, machineOptions);

  // Services
  const websocketServiceRef = useRef<WebSocketService | null>(null);
  const audioManagerRef = useRef<WebAudioManager | null>(null);
  
  // 🔧 FIX: Use ref to track latest machineState to avoid stale closure in callbacks
  // This is critical for callbacks created in useEffect with [] dependencies
  const machineStateRef = useRef(machineState);
  machineStateRef.current = machineState;
  
  // Store system instructions for connection
  const systemInstructionsRef = useRef<string>('');
  
  // Capture sessionTrackingId at mount time to prevent race conditions
  const boundSessionTrackingIdRef = useRef<string | undefined>(config.sessionTrackingId);
  
  // Store session config to use on connect
  const pendingSessionConfigRef = useRef<any>(null);

  // AI response timestamp for message ordering
  const aiResponseStartedTimestampRef = useRef<number | null>(null);

  // 🔧 FIX: Track if we've received the first audio chunk of this AI response
  // This prevents the race condition where audio chunks arrive before text deltas
  // Without this, isFirstChunk would be true for EVERY audio chunk until text arrives,
  // causing the buffer to be reset multiple times and losing early audio
  const hasReceivedFirstAudioChunkRef = useRef<boolean>(false);

  // Derive WebSocketSessionState from machine state for backward compatibility
  const deriveStateFromMachine = useCallback((mState: typeof machineState): WebSocketSessionState => {
    return {
      connectionStatus: getConnectionStatus(mState),
      retryCount: mState.context.retryCount,
      error: mState.context.error,
      isRecording: mState.context.isRecording || mState.matches({ audio: { recording: 'active' } }),
      isPlaying: mState.context.isPlaying || mState.matches({ audio: { playback: 'active' } }),
      audioLevel: mState.context.audioLevel,
      qualityStats: null, // Not tracked in machine
      transcriptInProgress: mState.context.transcriptInProgress,
      currentTranscriptId: mState.context.currentTranscriptId,
      aiResponseInProgress: mState.context.aiResponseInProgress,
      messages: mState.context.messages,
      userTranscriptComplete: mState.context.userTranscriptComplete,
      isUserSpeaking: machineIsUserSpeaking(mState),
      isAISpeaking: machineIsAISpeaking(mState),
      aiTextResponseComplete: mState.context.aiTextResponseComplete,
      userSpeechStoppedTimestamp: mState.context.userSpeechStoppedTimestamp,
      inputMode: mState.context.inputMode,
      avatarAudioEnabled: mState.context.avatarAudioEnabled,
      microphoneFallback: mState.context.microphoneFallback,
      analysisMode: mState.context.analysisMode,
      analysisModeProcessing: mState.context.analysisModeProcessing,
    };
  }, []);

  // Get current state derived from machine
  const state = deriveStateFromMachine(machineState);

  // Initialize Audio Controller hook
  const audioController = useAudioController({
    send,
    websocketServiceRef,
    audioManagerRef,
    aiResponseStartedTimestampRef,
    hasReceivedFirstAudioChunkRef,
  });

  // Initialize Speech Lifecycle hook (no longer needs speechCorrelator)
  // Messages are created with server's item_id directly, eliminating correlation
  const speechLifecycleWithAudioBlock = useSpeechLifecycle({
    send,
    websocketServiceRef,
    audioManagerRef,
    audioBlockedRef: audioController.audioBlockedRef,
    hasReceivedFirstAudioChunkRef,
  });

  // Initialize Transcript Manager hook (no longer needs speechCorrelator or orphan refs)
  // Transcripts are matched by item_id directly, no correlation needed
  const transcriptManager = useTranscriptManager({
    send,
    boundSessionTrackingIdRef,
    aiResponseStartedTimestampRef,
    sessionTracking: config.sessionTracking,
  });

  // Initialize Connection Manager hook
  const connectionManager = useConnectionManager({
    config,
    send,
    websocketServiceRef,
    audioManagerRef,
    systemInstructionsRef,
    pendingSessionConfigRef,
    isRecordingForBlobRef: speechLifecycleWithAudioBlock.isRecordingForBlobRef,
    recordedAudioChunksRef: speechLifecycleWithAudioBlock.recordedAudioChunksRef,
  });

  // Initialize Input Mode hook
  const inputMode = useInputMode({
    send,
    websocketServiceRef,
    audioManagerRef,
    boundSessionTrackingIdRef,
    sessionTracking: config.sessionTracking,
    isRecordingForBlobRef: speechLifecycleWithAudioBlock.isRecordingForBlobRef,
    recordedAudioChunksRef: speechLifecycleWithAudioBlock.recordedAudioChunksRef,
    resetSpeechState: speechLifecycleWithAudioBlock.resetSpeechState,
  });

  // Initialize Analysis Mode hook
  const analysisMode = useAnalysisMode({
    send,
    websocketServiceRef,
    systemInstructionsRef,
  });

  // Update session (send instructions to OpenAI)
  const updateSession = useCallback(async (sessionConfig: any) => {
    if (sessionConfig.instructions) {
      systemInstructionsRef.current = sessionConfig.instructions;
    }
    
    pendingSessionConfigRef.current = sessionConfig;
    logger.debug('[WebSocketSession] 🎙️ updateSession - storing voice:', sessionConfig.voice);
    
    const websocketService = websocketServiceRef.current;
    if (websocketService && websocketService.isConnected()) {
      await websocketService.updateSessionConfig(sessionConfig);
    }
  }, []);

  // Initialize Context Manager hook for summarization
  const contextManager = useContextManager({
    messages: state.messages,
    websocketServiceRef,
    systemInstructionsRef,
    updateSession,
    maxMessages: 20,
    keepMessages: 10,
  });

  // Initialize services and wire up callbacks
  useEffect(() => {
    // Initialize Audio Manager
    audioManagerRef.current = new WebAudioManager();
    const audioManager = audioManagerRef.current;
    
    // Initialize WebSocket service with ALL callbacks
    websocketServiceRef.current = new WebSocketService({
      // Connection status
      onConnectionStatusChange: (status) => {
        if (status === 'connected') {
          send({ type: 'CONNECTED' });
          connectionManager.retryAttemptRef.current = 0;
          connectionManager.clearRetryTimer();
        } else if (status === 'disconnected') {
          if (!connectionManager.userInitiatedDisconnectRef.current) {
            // Connection lost unexpectedly - attempt reconnection
            send({ type: 'CONNECTION_ERROR', error: new Error('Connection lost unexpectedly') });
            
            // Trigger retry if enabled
            if (config.retryOnError && !connectionManager.retryHaltedRef.current) {
              connectionManager.scheduleRetry('connection_lost');
            }
          }
        } else if (status === 'error') {
          // Error event will be sent separately
        }
      },
      
      // Audio events
      onAudioDataReceived: (pcm16Data) => {
        if (audioController.audioBlockedRef.current) {
          logger.debug('[🎯 INTERRUPT] 🚫 BLOCKED - Ignoring audio chunk');
          return;
        }

        // 🔧 FIX: Detect first audio chunk using dedicated ref (not XState)
        // 
        // CRITICAL BUG FIXED: Previously used machineIsAISpeaking() and aiResponseInProgress
        // to detect first chunk. But those are updated by TEXT events (AI_RESPONSE_DELTA),
        // not AUDIO events. Audio chunks often arrive BEFORE text chunks!
        // 
        // Race condition scenario:
        // 1. Audio chunk #1 arrives → isFirstChunk=true (correct) → reset buffer
        // 2. Audio chunk #2 arrives BEFORE any text → machine still in 'waiting',
        //    aiResponseInProgress still empty → isFirstChunk=true (WRONG!) → RESET BUFFER
        // 3. Chunks 1's audio is LOST, only later chunks play
        // 
        // Fix: Use hasReceivedFirstAudioChunkRef to track audio independently of text
        const isFirstChunk = !hasReceivedFirstAudioChunkRef.current;

        if (isFirstChunk) {
          // Mark that we've received the first audio chunk of this response
          hasReceivedFirstAudioChunkRef.current = true;
          
          // Set timestamp for message ordering
          if (aiResponseStartedTimestampRef.current == null) {
            aiResponseStartedTimestampRef.current = Date.now();
          }
          
          // Check if previous audio is still playing (shouldn't happen normally)
          const isAudioStillPlaying = audioManagerRef.current?.isStreamingPlaybackActive() ?? false;
          if (isAudioStillPlaying) {
            console.warn('[🔊 STREAM] ⚠️ New response detected while audio still playing - resetting buffer');
          }
          
          // Reset buffer for new response.
          // Soft reset: keeps master audio chain connected so the transition between
          // responses doesn't produce a click (particularly on Firefox).
          audioManagerRef.current?.softResetForNextResponse();

          // 🛡️ CRITICAL: Reset VAD state so vadWasOpen=false.
          // prepareForNextSpeech() (called on speech_stopped) intentionally keeps
          // vadWasOpen=true to allow continued speech detection. But once the avatar
          // starts speaking, we MUST stop streaming mic audio to OpenAI — otherwise
          // AEC echo residue is interpreted as user speech and the avatar interrupts
          // itself. The VAD's playbackActive protection only guards gate *opening*,
          // not the already-open vadWasOpen bypass.
          audioManagerRef.current?.resetVADState();

          // 🎯 Text-audio sync: Signal that audio has been received for this
          // response. This sets hasReceivedAudioForResponse=true (via setPlayingTrue)
          // so the aiFinishing guard knows audio is expected even if AI_RESPONSE_DONE
          // arrives during prebuffering (isPlaying=false in the audio engine, but
          // audio IS coming). Without this, AI_RESPONSE_DONE during prebuffering
          // would finalize immediately, showing full text while audio hasn't played.
          // Also makes isPlaying=true in context so useSyncedTextReveal shows
          // throttled text (not full) during prebuffering — fixes back-and-forth.
          send({ type: 'PLAYBACK_STARTED' });

          logger.debug('[🔊 STREAM] First audio chunk detected - buffer reset + VAD reset');
        }

        audioManagerRef.current?.playPCM16DataStreaming(pcm16Data);
      },
      
      onAudioDone: () => {
        logger.debug('[WebSocket] AI audio done');
        audioManagerRef.current?.finishStreamingResponse();
        send({ type: 'AI_AUDIO_DONE' });
        
        // 🔧 FIX: Reset the first audio chunk flag for the next response
        hasReceivedFirstAudioChunkRef.current = false;
      },
      
      // Transcript events
      onTranscriptReceived: transcriptManager.handleTranscriptReceived,
      
      // Speech detection events
      onUserSpeechStarted: speechLifecycleWithAudioBlock.handleUserSpeechStarted,
      onUserSpeechStopped: speechLifecycleWithAudioBlock.handleUserSpeechStopped,
      
      // AI response events
      onAIResponseDelta: transcriptManager.handleAIResponseDelta,
      onAIResponseDone: transcriptManager.handleAIResponseDone,
      
      // Function call events (Analysis Mode)
      onFunctionCall: (callId, name, args) => {
        // A function call response carries no audio, so onAudioDone never fires and
        // hasReceivedFirstAudioChunkRef stays true. That would suppress the
        // softResetForNextResponse() call on the first audio chunk of the next
        // response, leaving jitterDetected / chunkArrivalTimes stale from the
        // pre-tool-call response and causing the NetworkQualityPill to appear
        // (and stick) as if there were real network degradation.
        hasReceivedFirstAudioChunkRef.current = false;
        analysisMode.handleFunctionCall(callId, name, args);
      },
      
      // Error events
      onError: (error) => {
        send({ type: 'CONNECTION_ERROR', error });
        if (!connectionManager.userInitiatedDisconnectRef.current) {
          // Retry handled by connection manager
        }
      },
    }, boundSessionTrackingIdRef.current, config.sessionTracking);

    const websocketService = websocketServiceRef.current;

    // Setup Audio Manager event listeners
    audioManager.on('onPlaybackStart', () => {
      logger.debug('[🔊 WebSocket] Audio playback STARTED');
      send({ type: 'PLAYBACK_STARTED' });
    });

    audioManager.on('onPlaybackStop', () => {
      logger.debug('[🔊 WebSocket] Audio playback STOPPED');
      send({ type: 'PLAYBACK_STOPPED' });
    });

    audioManager.on('onAudioLevel', (level: AudioLevel) => {
      send({ type: 'AUDIO_LEVEL_CHANGED', level });
    });

    audioManager.on('onRecordingStart', () => {
      send({ type: 'RECORDING_STARTED' });
    });

    audioManager.on('onRecordingStop', () => {
      send({ type: 'RECORDING_STOPPED' });
    });

    audioManager.on('onError', (error: Error) => {
      send({ type: 'CONNECTION_ERROR', error });
    });
    
    // Handle false positives (non-human sounds)
    audioManager.on('onGateForceClosed', speechLifecycleWithAudioBlock.handleGateForceClosed);
    
    // Listen for client-side VAD speech detection
    audioManager.on('onSpeechDetected', speechLifecycleWithAudioBlock.handleSpeechDetected);

    // Listen for early "speech likely" signal (waveform feedback before gate opens)
    audioManager.on('onSpeechLikely', speechLifecycleWithAudioBlock.handleSpeechLikely);

    // 🛡️ Watchdog: speech_stopped never arrived after prolonged silence
    audioManager.on('onSpeechStoppedTimeout', speechLifecycleWithAudioBlock.handleSpeechStoppedTimeout);
    
    // Listen for speech detected during PTT hold — interrupt avatar immediately
    // (instead of interrupting on button press, we wait until user actually speaks)
    audioManager.on('onPTTSpeechDetected', () => {
      logger.debug('[🎙️ PTT] Speech detected during hold - interrupting avatar');
      audioController.cancelResponse();
    });

    // Initialize AudioContext early
    const initializeAudio = async () => {
      try {
        logger.info('[🎙️ AEC] 🚀 Initializing shared AudioContext early...');
        await audioManager.initializeAudio();
        logger.info('[🎙️ AEC] ✅ Shared AudioContext ready for both mic and avatar');
      } catch (error) {
        console.error('Failed to initialize audio:', error);
        send({ type: 'CONNECTION_ERROR', error: error as Error });
      }
    };

    initializeAudio();

    // Cleanup on unmount
    return () => {
      connectionManager.clearRetryTimer();
      websocketService?.disconnect();
      audioManager.cleanup();
    };
  }, []);

  // Note: Cleanup interval for orphans removed - no longer needed with new design
  // Messages always have valid server item_id, no orphans possible

  // Auto-connect if configured
  useEffect(() => {
    if (config.autoConnect && state.connectionStatus === 'disconnected') {
      connectionManager.connect();
    }
  }, [config.autoConnect, state.connectionStatus, connectionManager.connect]);

  // 🎯 Text-audio sync: Safety timeout for aiFinishing state.
  // When AI text completes but audio is still playing, we enter aiFinishing and
  // wait for PLAYBACK_STOPPED. If that event never fires (analyser failure, audio
  // error), this timeout forces finalization so the conversation doesn't get stuck.
  const isAiFinishing = machineState.matches({ conversation: 'aiFinishing' });
  useEffect(() => {
    if (!isAiFinishing) return;

    // 🎯 Text-audio sync: Safety timeout for aiFinishing state.
    // Instead of a fixed 10s timeout (which fires while audio is still playing
    // if text completed early — the "sem itálico" bug), we poll every 500ms.
    // We only force finalization if audio has NOT been playing for 3 consecutive
    // checks (1.5s of silence). This ensures we never finalize while audio is
    // actively playing, while still recovering from stuck states.
    const POLL_INTERVAL_MS = 500;
    const SILENCE_REQUIRED_CHECKS = 3; // 1.5s of no audio before forcing finalize
    let silenceCheckCount = 0;

    logger.debug('[🎯 SYNC] Entering aiFinishing state — starting audio-active polling');
    const intervalId = setInterval(() => {
      const isAudioActive = audioManagerRef.current?.isStreamingPlaybackActive() ?? false;
      if (isAudioActive) {
        // Audio is still playing — reset silence counter, don't finalize
        silenceCheckCount = 0;
      } else {
        silenceCheckCount++;
        if (silenceCheckCount >= SILENCE_REQUIRED_CHECKS) {
          logger.warn('[🎯 SYNC] aiFinishing: audio inactive for 1.5s — forcing finalization');
          send({ type: 'AI_FINISHING_TIMEOUT' });
        }
      }
    }, POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [isAiFinishing, send]);

  // Computed values
  const isConnected = machineIsConnected(machineState);
  const canRecord = isConnected && !state.isRecording;
  const currentVolume = audioController.getCurrentVolume();

  // Update configuration (not supported at runtime)
  const updateConfig = useCallback((_newConfig: Partial<WebSocketSessionConfig>) => {
    console.warn('[WebSocket] Runtime config updates not supported - requires reconnection');
  }, []);

  // Trigger initial greeting
  // @param delayMs - optional delay before triggering
  // @param _avatarRole - (unused, kept for backwards compat) role is read from config.characterRole
  // @param customTrigger - optional custom trigger message (e.g. for restored sessions)
  const triggerInitialGreeting = useCallback(async (delayMs?: number, _avatarRole?: string, customTrigger?: string) => {
    const websocketService = websocketServiceRef.current;
    if (!websocketService || !websocketService.isConnected()) {
      console.warn('[WebSocket] Not connected - cannot trigger initial greeting');
      return;
    }
    
    if (delayMs && delayMs > 0) {
      await new Promise(resolve => setTimeout(resolve, delayMs));
    }
    
    const avatarRole = config.characterRole || 'character';
    const systemTrigger = customTrigger || `SYSTEM: You are ${avatarRole} in this moment. Engage naturally.`;
    
    logger.info('[🎬 GREETING] Triggering initial greeting with role:', avatarRole, customTrigger ? '(custom restore trigger)' : '');
    websocketService.sendText(systemTrigger);
  }, [config.characterRole]);

  // Load a saved transcript into the message history (for save/restore)
  const loadTranscript = useCallback((messages: Array<{ text: string; timestamp: number; speaker: 'user' | 'ai'; id?: string }>) => {
    logger.info('[📜 RESTORE] Loading transcript with', messages.length, 'messages');
    messages.forEach(msg => {
      send({ type: 'ADD_MESSAGE', message: msg });
    });
  }, [send]);

  // Stable identity for diagnostics getters. audioManagerRef is a ref, so these never
  // need to change. Consumers (e.g. NetworkQualityPill) use this as a polling effect
  // dependency — a fresh identity each render would tear down and recreate their interval.
  const getAudioDiagnostics = useCallback(() => audioManagerRef.current?.getDiagnostics() ?? null, []);

  // Send a system message (not shown in transcript, triggers AI response)
  // Used for silence nudges and other system-level prompts
  const sendSystemMessage = useCallback((text: string) => {
    const websocketService = websocketServiceRef.current;
    if (!websocketService || !websocketService.isConnected()) {
      console.warn('[WebSocket] Not connected - cannot send system message');
      return;
    }

    logger.info('[📨 SYSTEM] Sending system message:', text.substring(0, 80));
    websocketService.sendText(text);
  }, []);

  // Inject a context item WITHOUT triggering a response (per-turn re-priming).
  const injectContextItem = useCallback((text: string) => {
    const websocketService = websocketServiceRef.current;
    if (!websocketService || !websocketService.isConnected()) {
      return;
    }
    websocketService.injectContextItem(text);
  }, []);

  return {
    state,
    
    // Connection
    connect: connectionManager.connect,
    disconnect: connectionManager.disconnect,
    
    // Recording
    startRecording: audioController.startRecording,
    stopRecording: audioController.stopRecording,
    pauseMicrophone: audioController.pauseMicrophone,
    resumeMicrophone: audioController.resumeMicrophone,
    
    // Playback
    pausePlayback: audioController.pausePlayback,
    resumePlayback: audioController.resumePlayback,
    setVolume: audioController.setVolume,
    
    // Transcript
    clearTranscript: transcriptManager.clearTranscript,
    clearError: transcriptManager.clearError,
    
    // Session
    updateConfig,
    updateSession,
    triggerInitialGreeting,
    sendSystemMessage,
    injectContextItem,
    loadTranscript,
    
    // Audio
    resumeAudioContext: audioController.resumeAudioContext,
    getAudioFrequencyData: audioController.getAudioFrequencyData,
    getMicrophoneFrequencyData: audioController.getMicrophoneFrequencyData,
    getAudioDiagnostics,
    setAtypicalVoiceMode: (enabled: boolean) => audioManagerRef.current?.setAtypicalVoiceMode(enabled),
    // 🎯 Text-audio sync: playback position getters for synced text reveal
    getAudioElapsedMs: () => audioManagerRef.current?.getElapsedPlaybackMs() ?? 0,
    getAudioTotalMs: () => audioManagerRef.current?.getTotalAudioDurationMs() ?? 0,
    
    // Input mode (keyboard / voice / PTT)
    setInputMode: inputMode.setInputMode,
    toggleAvatarAudio: inputMode.toggleAvatarAudio,
    sendTextMessage: inputMode.sendTextMessage,
    handlePTTPressed: inputMode.handlePTTPressed,
    handlePTTReleased: inputMode.handlePTTReleased,
    cancelResponse: audioController.cancelResponse,
    
    // Utils
    isConnected,
    canRecord,
    currentVolume,
    
    // Analysis Mode
    enterAnalysisMode: analysisMode.enterAnalysisMode,
    exitAnalysisMode: analysisMode.exitAnalysisMode,
    sendFunctionResult: analysisMode.sendFunctionResult,
    setAnalysisModeCallbacks: analysisMode.setAnalysisModeCallbacks,
    getCurrentInstructions: analysisMode.getCurrentInstructions,
    
    // Context Management
    isSummarizing: contextManager.isSummarizing,
    currentSummary: contextManager.currentSummary,
    forceSummarize: contextManager.forceSummarize,
  };
};

export default useWebSocketSession;
